import { Controller } from "@hotwired/stimulus"
import { HTMLInputElement } from "happy-dom"
import { isHTMLElement, isHTMLInputElement } from "~/types/types.util"
import { makeClickable } from "~/helpers/dom_helpers"

const WRAPPER_CLASS = ".bulk-select-wrapper"

// Connects to data-controller="bulk-select"
export default class BulkSelectController extends Controller<HTMLBodyElement> {
  static targets = ["masterCheckbox", "checkbox", "count", "form"]

  declare readonly masterCheckboxTarget: HTMLInputElement
  declare readonly checkboxTargets: HTMLInputElement[]
  declare readonly countTarget: HTMLElement
  declare readonly formTargets: HTMLFormElement[]

  private mode: "global" | "explicit" = "explicit"
  // This map holds cleanup functions for any event listeners we add to wrapper elements
  private cleanups = new Map<HTMLElement, () => void>()
  private wrapperClickListenerEnabled = false

  get isSelecting() {
    return this.mode === "global" || this.checkboxTargets.some((i) => i.checked)
  }

  connect() {
    this.refresh()
    // Enable TurboStream to close offcanvas via custom events
    this.element.addEventListener("bulk-select:close", () => this.abort())
  }

  disconnect() {
    this.element.removeEventListener("bulk-select:close", () => this.abort())
    this.disableWrapperClickListener()
  }

  toggleWrapperClickListener() {
    if (this.isSelecting) {
      // Check if this is the **first** time enabling wrapper click
      if (!this.wrapperClickListenerEnabled) {
        this.enableWrapperClickListener()
      }
    } else {
      this.disableWrapperClickListener()
    }
  }

  enableWrapperClickListener() {
    this.element.querySelectorAll<HTMLElement>(WRAPPER_CLASS).forEach((wrapper) => {
      // Avoid adding multiple listeners to the same wrapper
      if (this.cleanups.has(wrapper)) return

      // Make the entire wrapper clickable to toggle the checkbox
      this.addWrapperClickListener(wrapper)
    })
    this.wrapperClickListenerEnabled = true
  }

  disableWrapperClickListener() {
    this.cleanups.forEach((cleanup) => cleanup())
    this.cleanups.clear()
    this.wrapperClickListenerEnabled = false
  }

  addWrapperClickListener(wrapper: HTMLElement) {
    const cleanup = makeClickable(wrapper, this.onWrapperClick.bind(this))
    this.cleanups.set(wrapper, cleanup)
  }

  onWrapperClick(event: Event) {
    const wrapper = event.currentTarget
    if (!isHTMLElement(wrapper)) return
    const checkbox = wrapper.querySelector("input[type='checkbox']")

    // Cancel any clicks inside the wrapper except on the checkbox itself
    if (checkbox !== event.target && this.isSelecting) {
      event.preventDefault()
    } else {
      return
    }

    // If we get here, we need to toggle the checkbox manually. This means we clicked anywhere on the wrapper
    if (checkbox && isHTMLInputElement(checkbox)) {
      checkbox.checked = !checkbox.checked

      // Manually trigger the change event
      checkbox.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  // User toggled the "Select all" master checkbox
  toggleAll(event: Event) {
    const master = isHTMLInputElement(event.currentTarget) ? event.currentTarget : null
    if (!master) return

    // enter global select mode
    this.mode = "global"
    if (master.checked) {
      this.checkboxTargets.forEach((item) => (item.checked = true))
    } else {
      this.checkboxTargets.forEach((item) => (item.checked = false))
    }

    this.toggleWrapperClickListener()
    this.refresh()
  }

  selectAll() {
    this.mode = "global"
    this.masterCheckboxTarget.checked = true
    this.masterCheckboxTarget.indeterminate = false
    this.checkboxTargets.forEach((item) => (item.checked = true))
    this.enableWrapperClickListener()
    this.refresh()
  }

  abort() {
    this.mode = "explicit"
    this.masterCheckboxTarget.checked = false
    this.checkboxTargets.forEach((item) => (item.checked = false))
    this.disableWrapperClickListener()
    this.refresh()
  }

  // User toggled an individual checkbox
  toggleOne() {
    if (this.mode === "global") {
      // If they uncheck *any* item while global was active,
      // switch to explicit mode with only currently visible selections.
      if (this.checkboxTargets.some((item) => !item.checked)) {
        this.mode = "explicit"
      }
    }

    // Keep master checkbox in sync only when explicit
    if (this.mode === "explicit") {
      const checkedCount = this.checkboxTargets.filter((item) => item.checked).length

      this.masterCheckboxTarget.checked = checkedCount === this.checkboxTargets.length
      this.masterCheckboxTarget.indeterminate = checkedCount > 0 && checkedCount < this.checkboxTargets.length
    }

    this.toggleWrapperClickListener()
    this.refresh()
  }

  refresh() {
    this.updateCount()
    this.updateForms()
    this.updateActiveClass()
  }

  // Updates the counter display
  updateCount() {
    if (this.mode === "global") {
      this.countTarget.textContent = "All selected"
    } else {
      const selected = this.visibleSelected().length
      this.countTarget.textContent = selected === 0 ? `Select all` : `${selected} selected`
    }
  }

  updateForms() {
    const selected = this.visibleSelected()
    this.formTargets.forEach((form) => {
      // Clear existing hidden inputs
      form.querySelectorAll("input[name='ids[]'], input[data-bulk-select-filter]").forEach((input) => input.remove())

      if (this.mode === "explicit") {
        // Add hidden inputs for each selected ID
        selected.forEach((id) => {
          const hiddenInput = document.createElement("input")
          hiddenInput.type = "hidden"
          hiddenInput.name = "ids[]"
          hiddenInput.value = id
          form.appendChild(hiddenInput)
        })
      } else {
        // global mode: intentionally send no ids[] so server interprets as "global", but only within the query
        // params currently applied to the page (the filters). Otherwise it would act on every nuggit.
        this.appendFilterInputs(form)
      }
    })
  }

  appendFilterInputs(form: HTMLFormElement) {
    new URLSearchParams(window.location.search).forEach((value, name) => {
      const hiddenInput = document.createElement("input")
      hiddenInput.type = "hidden"
      hiddenInput.name = name
      hiddenInput.value = value
      // bulkSelectFilter is a marker. #updateForms runs every time the selection changes, and it first deletes the
      // inputs it added last time, so they don't pile up.
      hiddenInput.dataset.bulkSelectFilter = ""
      form.appendChild(hiddenInput)
    })
  }

  updateActiveClass() {
    // Toggle active class on the controller element
    if (this.isSelecting) {
      this.element.classList.add("bulk-selecting")
    } else {
      this.element.classList.remove("bulk-selecting")
    }
  }

  visibleSelected(): string[] {
    return this.checkboxTargets.filter((i) => i.checked).map((i) => i.value)
  }

  checkboxTargetConnected(item: HTMLInputElement) {
    // If global mode is active, auto-check it
    if (this.mode === "global") {
      item.checked = true
    }

    // Keep master synced in explicit mode
    if (this.mode === "explicit") {
      this.masterCheckboxTarget.checked = this.checkboxTargets.every((i) => i.checked)
    }

    if (this.isSelecting) {
      const wrapper = item.closest(WRAPPER_CLASS)
      if (wrapper && isHTMLElement(wrapper)) {
        this.addWrapperClickListener(wrapper)
      }
    }

    this.refresh()
  }
}
